// The ONE place colors live. Documented once, in CLAUDE.md → "Design tokens".
//
// Every surface — the app (Scorify.jsx), the auth-loading spinner (App.js), the
// Sentry error boundary (index.js), the waitlist page — imports T from here.
// Nothing restates hex values anywhere else; the only sanctioned exception is
// api/waitlist.js's email HTML, which cannot import modules.
//
// The shipping default is LIGHT: themeMode falls back to "light" and nothing in
// the codebase writes localStorage.scorify_theme — the dark palette exists but
// is unreachable without hand-planting that key.

export const THEMES = {
  light: {
    bg: "#faf8f3", surface: "#ffffff", surfaceAlt: "#f5f2ea",
    accent: "#8b1e2d", accentPressed: "#6f1622",
    // accentFill: the color of FILLED controls (buttons, active nav pill).
    // Identical to accent in light; exists because the redesign dark theme
    // needs crimson fills but pink links, two roles one token cannot serve.
    accentFill: "#8b1e2d",
    // textMuted/textDim darkened 2026-09-01: the previous #8a857c (3.28:1) and
    // #b3a89a (2.09:1) both failed WCAG AA (4.5:1) on these backgrounds.
    // textMuted moved #6e6960 → #5e5a52 with the 2026-09-27 redesign (the
    // mockups' muted gray; darker = safer on glass, ~6.4:1 on white).
    text: "#1a1a1a", textMuted: "#5e5a52", textDim: "#746d64",
    border: "#e8e1d3", borderStrong: "#d8c9b0",
    inputBg: "#ffffff",
    // warning darkened from #b8860b (2.91:1 — failed AA).
    success: "#2d6a4f", warning: "#8f6808", danger: "#c0392b",
    // moodMid: the 7-8 mood band, between success (9-10) and warning (5-6).
    // Replaced the hardcoded #7a9b6e (3.1:1 — failed AA at body sizes) in the
    // accessibility pass 2026-09-12; olive-green, 5.2:1 on bg / 5.8:1 on
    // surface, and white text on it is 5.8:1. The mood scale reads
    // success → moodMid → warning → danger, good to bad.
    moodMid: "#47703a",
    overlayLight: (a) => `rgba(0,0,0,${a})`,
  },
  // Dark REPLACED WHOLESALE 2026-09-27 (redesign Phase 1): the old amber dark
  // was unreachable (nothing writes scorify_theme) and the redesign dark is
  // crimson glass (docs/REDESIGN.md §2, from HomeActivityDark). Dark stays
  // unreachable until redesign Phase 7 ships a toggle, after every screen is
  // verified against these values.
  dark: {
    bg: "#111214", surface: "#1a1b1e", surfaceAlt: "#1f2024",
    accent: "#ff8a96", accentPressed: "#e0566a",
    accentFill: "#8b1e2d",
    text: "#f2f2f0", textMuted: "#a9a9ae", textDim: "#8a8a90",
    border: "rgba(255,255,255,0.14)", borderStrong: "rgba(255,255,255,0.24)",
    inputBg: "rgba(255,255,255,0.06)",
    success: "#5fbf8f", warning: "#d4a72c", danger: "#ff6b6b",
    moodMid: "#7fae6a",
    overlayLight: (a) => `rgba(242,242,240,${a})`,
  },
};

// Phase 7 (2026-09-28): dark mode ENABLED. An explicit user choice in
// localStorage.scorify_theme ('light' | 'dark', set by the Appearance
// control in Profile) wins; otherwise the OS setting decides. Resolved once
// at load — the Appearance control writes the key and reloads, because T/G
// are module constants read by every style in the app.
const storedTheme = (() => {
  try { const v = localStorage.getItem("scorify_theme"); return v === "light" || v === "dark" ? v : null; } catch { return null; }
})();
const systemDark = (() => {
  try { return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches; } catch { return false; }
})();
export const themeMode = storedTheme || (systemDark ? "dark" : "light");

// The theme that SHOULD be showing right now, recomputed live (stored choice
// wins, else the OS setting). themeMode above is the value resolved at load
// and actually rendered; when effectiveTheme() diverges from it — an OS
// appearance change, or a change made while backgrounded — the app needs a
// fresh load to pick it up (T/G are module constants). Same resolution order
// as the load-time computation, so "System" only ever follows the OS when no
// explicit choice is stored.
export function effectiveTheme() {
  let stored = null;
  try { const v = localStorage.getItem("scorify_theme"); if (v === "light" || v === "dark") stored = v; } catch {}
  if (stored) return stored;
  let dark = false;
  try { dark = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches; } catch {}
  return dark ? "dark" : "light";
}

export const T = THEMES[themeMode];

// Derive an alpha'd tint from a token instead of hardcoding an rgba() — e.g.
// withAlpha(T.accent, 0.18) for focus rings and soft fills. Hex in, rgba out;
// passes through non-hex values (the dark theme's rgba borders) unchanged.
export const withAlpha = (hex, a) => {
  if (typeof hex !== "string" || hex[0] !== "#") return hex;
  const h = hex.length === 4 ? hex.slice(1).split("").map(c => c + c).join("") : hex.slice(1);
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// ── Warm Liquid Glass tokens (redesign 2026-09-27, docs/REDESIGN.md §2) ─────
// Values extracted from docs/redesign/scorify-redesign/chosen/. Same rule as
// THEMES: these are the ONLY place the glass recipe lives; JSX composes from
// GLASS/RADII/RINGS, never restates an rgba.

const mq = (q) => {
  try { return typeof window !== "undefined" && window.matchMedia && window.matchMedia(q).matches; } catch { return false; }
};
// Accessibility switches, resolved once at load (docs/REDESIGN.md §5).
export const REDUCED_TRANSPARENCY = mq("(prefers-reduced-transparency: reduce)");
export const REDUCED_MOTION = mq("(prefers-reduced-motion: reduce)");

export const RADII = { card: 28, chip: 26, tabBar: 32, stat: 22 };

// GLASS_TINT — the cool blue-white cast of the glass fill (contrasts the
// warm crimson background without going purple/mauve).
// THE DIAL: edit these RGB triples to tune coolness. Higher blue = cooler.
// Light default "246,250,254" is one step off pure white; try "240,246,254"
// for cooler, "255,255,255" for none. Keep R within ~10 of B — a large
// blue-red gap over the crimson glow is what reads mauve.
const GLASS_TINT_LIGHT = "246,250,254";
const GLASS_TINT_DARK = "235,242,252";

// AMBIENT_STRENGTH — THE dial for how much color sits behind the glass.
// It multiplies every glow alpha in both themes: 1 = shipped look,
// 0.7 = calmer, 1.3 = warmer. This is the ONE value to change.
const AMBIENT_STRENGTH = 1;
// One blob: [x, yUnderlay, radiusPx, "r,g,b", alpha]. y is measured from the
// ambient underlay's top, which sits 120px below the viewport top.
const ambientFrom = (blobs) => blobs.map(([x, y, r, rgb, a]) => {
  const peak = Math.min(1, a * AMBIENT_STRENGTH);
  return `radial-gradient(circle ${r}px at ${x} ${y}px, rgba(${rgb},${peak.toFixed(3)}) 0%, rgba(${rgb},${(peak * 0.38).toFixed(3)}) 48%, transparent 72%)`;
}).join(",");

const GLASSES = {
  light: {
    glassTint: GLASS_TINT_LIGHT,
    // Round 5: the edge is a TWO-TONE HAIRLINE, uniform all the way around —
    // a 1px dark outer line (the element's real border, warm neutral) plus a
    // 1px light inner line (first inset shadow component). Dark-outside/
    // light-inside stays visible on cream, on the crimson glow, and in dark
    // mode — the white-only specular rim of round 4 vanished on cream. The
    // radial lens layer is gone (invisible once the hairline carries the
    // edge), so surfaces are plain tinted fills again; frost stays subtle
    // (fill + blur + tint unchanged — the hairline carries the edge).
    cardSurface: `rgba(${GLASS_TINT_LIGHT},0.20)`,
    washSurface: `rgba(${GLASS_TINT_LIGHT},0.10)`,
    chipSurface: `rgba(${GLASS_TINT_LIGHT},0.26)`,
    chipSelectedSurface: `rgba(${GLASS_TINT_LIGHT},0.65)`,
    // Fixed chrome (header, tab bar): REAL see-through glass (chrome pass
    // 2026-09-28). The old solid-ish fills (0.60/0.78) read as white blocks
    // on device; shapes behind now dissolve into soft tone because the
    // heavy blurFixed below does the hiding, not the fill. Label
    // readability over any backdrop comes from chromeTextShadow + the
    // pill's slightly stronger tint, never from opacity.
    chromeSurface: `rgba(${GLASS_TINT_LIGHT},0.34)`,
    chromeBg: `rgba(${GLASS_TINT_LIGHT},0.34)`,
    // Pill runs a slightly stronger tint than the bar — 0.65 is its AA
    // floor over a crimson button (accent label sampled 3.89:1 at 0.55,
    // 4.47 at 0.62, 4.79 at 0.65; blurred text lines remain visible
    // through it because the backdrop under light content stays light).
    pillSurface: `rgba(${GLASS_TINT_LIGHT},0.65)`,
    hairline: "1px solid rgba(64,48,42,0.11)",          // dark outer line
    hairlineInner: "rgba(255,255,255,0.6)",             // light inner line
    // First component of every shadow = the inner light line; the soft
    // inner glow (pane thickness) is toned down so it never muddies the
    // hairline. Cards carry NO drop shadow; chrome gets a tight NEUTRAL
    // umbra (crimson-tinted shadows read as pink halo on device).
    shadow: "inset 0 0 0 1px rgba(255,255,255,0.6), inset 0 0 12px rgba(255,255,255,0.08)",
    washShadow: "inset 0 0 0 1px rgba(255,255,255,0.45), inset 0 0 10px rgba(255,255,255,0.06)",
    chromeShadow: "inset 0 0 0 1px rgba(255,255,255,0.6), inset 0 0 10px rgba(255,255,255,0.08), 0 2px 10px rgba(30,22,22,0.10)",
    chipShadow: "inset 0 0 0 1px rgba(255,255,255,0.55), 0 1px 6px rgba(30,22,22,0.07)",
    chipSelectedShadow: "inset 0 0 0 1px rgba(255,255,255,0.7), 0 2px 8px rgba(30,22,22,0.10)",
    blurCard: "blur(26px) saturate(165%)",
    blurChip: "blur(20px) saturate(160%)",
    // Chrome blur, round 2 (device: 32px hid text entirely — only strong
    // color survived it). 12px is the iOS feel: text scrolling beneath
    // stays visible as soft unreadable lines, while the moderate fill +
    // tamer saturate keep a crimson button reading as a soft-edged tone
    // rather than a solid block.
    blurFixed: "blur(12px) saturate(150%)",
    // Light halo behind chrome labels (tab names, Ask Kai, header
    // wordmark) so they stay readable when dark or crimson content
    // scrolls beneath — the sanctioned "subtle text treatment".
    chromeTextShadow: "0 1px 2px rgba(255,255,255,0.7), 0 0 8px rgba(255,255,255,0.5)",
    // Ambient glow (round 6). Painted on a position:fixed UNDERLAY that
    // starts 120px below the viewport top (Scorify.jsx renders it) — iOS
    // treats backgrounds on -webkit-overflow-scrolling:touch scrollers as
    // content-attached regardless of backgroundAttachment, which is how
    // faint pink kept reaching the status bar; a fixed element with
    // top:120px physically cannot paint there, on any engine, at any
    // scroll. Coordinates below are UNDERLAY coords (viewport y − 120).
    // Round-6 experiment: color reaches the middle and lower screen —
    // five soft blobs alternating sides, crimson joined by peach and
    // rose so the glass has warm variation to frost. Crimson cores stay
    // off-canvas; peach/rose/pink are light enough to sit under cards.
    ambient: ambientFrom([
      ['-100px',        240, 300, '139,30,45',   0.37],  // crimson, upper-left (0.40 sampled 4.46:1 under the wellness disclaimer's bare-page text)
      ['calc(100% + 50px)', 200, 300, '238,178,128', 0.38],  // peach, upper-right (0.42 sampled 4.49:1 under habit-card right edge)
      ['-40px',         520, 280, '217,131,138', 0.34],  // rose, mid-left
      ['calc(100% + 20px)', 480, 300, '232,180,184', 0.34],  // pink, mid-right (core off-edge, dimmed twice: card text scrolling past its falloff sampled 4.07 then 4.31; 0.34 clears AA)
      ['35%',           730, 320, '244,200,168', 0.45],  // peach, lower band
    ]),
    ringHalo: "radial-gradient(circle, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.4) 60%, transparent 75%)",
    ringTrack: "rgba(26,26,26,0.07)",
    ringColors: ["#8b1e2d", "#a8434f", "#c26f78", "#d99aa0"],
    lowValue: "#c0392b",
    divider: "rgba(26,26,26,0.08)",
    // Modals + the coach drawer (Phase 6): a HIGH-fill frosted pane — forms
    // and check-in flows need near-solid backing for AA, so the fill does
    // the work and the blur only softens what leaks around the edges. The
    // scrim underneath dims AND blurs the page so backdrops genuinely block
    // the content (and the chrome is already hidden by the modal gate).
    modalSurface: `rgba(${GLASS_TINT_LIGHT},0.92)`,
    scrim: "rgba(17,16,16,0.55)",
    scrimBlur: "blur(10px)",
    // Inner tiles (goal rows, priority rows, checklist chips) sitting ON a
    // glass card: a light wash, never black-alpha — dark tint over a
    // see-through card was the one sub-AA text background left (goal-row
    // "255d left" over the pink glow sampled 4.20:1).
    tileBg: "rgba(255,255,255,0.5)",
    solidFallbackBg: "rgba(255,255,255,0.72)",
  },
  dark: {
    glassTint: GLASS_TINT_DARK,
    cardSurface: `rgba(${GLASS_TINT_DARK},0.05)`,
    washSurface: `rgba(${GLASS_TINT_DARK},0.03)`,
    chipSurface: `rgba(${GLASS_TINT_DARK},0.05)`,
    chipSelectedSurface: `rgba(${GLASS_TINT_DARK},0.14)`,
    chromeSurface: `rgba(${GLASS_TINT_DARK},0.06)`,
    chromeBg: `rgba(${GLASS_TINT_DARK},0.06)`,
    pillSurface: `rgba(30,32,36,0.55)`,
    hairline: "1px solid rgba(0,0,0,0.45)",
    hairlineInner: "rgba(255,255,255,0.25)",
    shadow: "inset 0 0 0 1px rgba(255,255,255,0.25), inset 0 0 12px rgba(255,255,255,0.03)",
    washShadow: "inset 0 0 0 1px rgba(255,255,255,0.16), inset 0 0 10px rgba(255,255,255,0.02)",
    chromeShadow: "inset 0 0 0 1px rgba(255,255,255,0.25), inset 0 0 10px rgba(255,255,255,0.03), 0 2px 10px rgba(0,0,0,0.4)",
    chipShadow: "inset 0 0 0 1px rgba(255,255,255,0.2), 0 1px 6px rgba(0,0,0,0.35)",
    chipSelectedShadow: "inset 0 0 0 1px rgba(255,255,255,0.3), 0 2px 8px rgba(0,0,0,0.4)",
    blurCard: "blur(26px) saturate(165%)",
    blurChip: "blur(20px) saturate(160%)",
    blurFixed: "blur(12px) saturate(150%)",
    chromeTextShadow: "0 1px 2px rgba(0,0,0,0.55), 0 0 8px rgba(0,0,0,0.4)",
    ambient: ambientFrom([
      ['-100px',        240, 300, '139,30,45',  0.34],  // dark: dimmed for AA margin (habit-card edge sampled 4.50 exact)
      ['calc(100% + 50px)', 200, 300, '255,122,107', 0.15],  // dark: dimmed (right card edge sampled 4.49 with light muted text)
      ['-40px',         520, 280, '139,30,45',  0.24],
      ['calc(100% + 20px)', 480, 300, '90,26,36',   0.50],
      ['35%',           730, 320, '255,140,110', 0.16],
    ]),
    ringHalo: "radial-gradient(circle, rgba(139,30,45,0.35) 0%, rgba(139,30,45,0.15) 60%, transparent 75%)",
    ringTrack: "rgba(255,255,255,0.08)",
    ringColors: ["#ff6b7d", "#e0566a", "#b8424f", "#8f3440"],
    lowValue: "#ff8a96",
    divider: "rgba(255,255,255,0.10)",
    modalSurface: "rgba(30,32,36,0.94)",
    scrim: "rgba(0,0,0,0.6)",
    scrimBlur: "blur(10px)",
    tileBg: "rgba(255,255,255,0.06)",
    solidFallbackBg: "rgba(35,36,40,0.92)",
  },
};

// prefers-reduced-transparency: solid readable surfaces, plain drawn border,
// no hairline shadows/blur/glow — one token switch.
const baseGlass = GLASSES[themeMode] || GLASSES.light;
export const G = REDUCED_TRANSPARENCY
  ? {
      ...baseGlass,
      cardSurface: THEMES[themeMode].surface,
      washSurface: THEMES[themeMode].surface,
      chipSurface: THEMES[themeMode].surfaceAlt,
      chipSelectedSurface: THEMES[themeMode].surface,
      chromeSurface: THEMES[themeMode].surface,
      chromeBg: THEMES[themeMode].surface,
      pillSurface: THEMES[themeMode].surface,
      hairline: `1px solid ${THEMES[themeMode].border}`,
      shadow: "none", washShadow: "none",
      chromeShadow: "0 2px 10px rgba(30,22,22,0.10)",
      chromeTextShadow: "none",
      chipShadow: "none", chipSelectedShadow: "none",
      tileBg: THEMES[themeMode].surfaceAlt,
      modalSurface: THEMES[themeMode].surface,
      scrimBlur: undefined,
      ambient: "none",
      ringHalo: "none",
      blurCard: undefined, blurChip: undefined, blurFixed: undefined,
    }
  : baseGlass;
